import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircle, ChevronDown, Gavel, Loader2, Pencil, Phone, Send, ShieldQuestion,
} from 'lucide-react';
import { practiceApi, errorDetailText } from '../../services/api';
import {
  AssistantAction, AssistantArtifact, AssistantResponse,
} from '../../types';
import { usePracticeStore } from '../../store/practiceStore';
import { MicButton } from '../ui/MicButton';
import { dayLabel, waLink } from './format';

type Msg = { role: 'user' | 'assistant'; content: string };

interface PracticeAssistantProps {
  /** Scope every turn to a case (case-detail usage). */
  matterId?: string;
  /** Scope to one hearing (pre-scoped "Log outcome" flow). */
  hearingId?: string;
  /** Static assistant bubble shown at the start of a pre-scoped session. */
  opener?: string;
  /** Start expanded (e.g. when opened from a "Log outcome" button). */
  autoExpand?: boolean;
  /** Called after an action is applied (parents refresh their local lists). */
  onExecuted?: () => void;
  className?: string;
}

const PLACEHOLDER = 'Ask or tell — "adjourned to Aug 3, call the client"';

/**
 * The Practice assistant — a docked chat that answers questions about the
 * diary (artifacts) and takes dictated updates (actions). Nothing is saved
 * until the user confirms an action card. Anatomy mirrors DraftIntakeChat:
 * optimistic send with rollback, auto-grow composer, MicButton (manual only).
 */
export function PracticeAssistant({
  matterId,
  hearingId,
  opener,
  autoExpand,
  onExecuted,
  className,
}: PracticeAssistantProps) {
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [error, setError] = useState('');
  const [latest, setLatest] = useState<AssistantResponse | null>(null);
  const [executed, setExecuted] = useState('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [expanded, setExpanded] = useState(!!autoExpand || !!opener);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  const invalidate = usePracticeStore((s) => s.invalidate);
  const fetchBoard = usePracticeStore((s) => s.fetchBoard);
  const board = usePracticeStore((s) => s.board);
  const cases = usePracticeStore((s) => s.cases);

  useEffect(() => {
    if (!expanded) return;
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages, isThinking, latest, executed, expanded]);

  // Auto-grow composer (same behavior as FramingChat).
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight + 2, 128)}px`;
  }, [input]);

  const send = async (text: string) => {
    const content = text.trim();
    if (!content || isThinking) return;
    setError('');
    setExecuted('');
    setExpanded(true);
    const history: Msg[] = [...messages, { role: 'user', content }];
    setMessages(history);
    setInput('');
    setIsThinking(true);
    try {
      const { data } = await practiceApi.assistant({
        messages: history.slice(-24),
        ...(matterId ? { matter_id: matterId } : {}),
        ...(hearingId ? { hearing_id: hearingId } : {}),
      });
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      setLatest(data);
    } catch (err: any) {
      setError(
        err?.response?.status === 429
          ? errorDetailText(err, 'Daily assistant limit reached — try again tomorrow.')
          : errorDetailText(err, 'The assistant is unavailable right now — please try again.'),
      );
      setMessages(history.slice(0, -1));
      setInput(content);
    } finally {
      setIsThinking(false);
    }
  };

  const confirmAction = async () => {
    const action = latest?.action;
    if (!action || isExecuting) return;
    setIsExecuting(true);
    setError('');
    try {
      const { data } = await practiceApi.assistantExecute(action);
      setExecuted(data.message || 'Done.');
      setLatest((l) => (l ? { ...l, action: null, ready: false } : l));
      invalidate();
      fetchBoard();
      onExecuted?.();
    } catch (err: any) {
      setError(errorDetailText(err, 'Could not apply that — nothing was saved.'));
    } finally {
      setIsExecuting(false);
    }
  };

  const cancelAction = () =>
    setLatest((l) => (l ? { ...l, action: null, ready: false } : l));

  /** Best-effort case title for a matter id from what's already cached. */
  const caseTitle = (id: string): string | null => {
    const fromCases = cases.find((c) => c.id === id)?.title;
    if (fromCases) return fromCases;
    if (!board) return null;
    const pools = [...board.today_hearings, ...board.tomorrow_hearings, ...board.needs_update];
    return pools.find((h) => h.matter_id === id)?.case_title ?? null;
  };

  const showConfirm = !!(latest?.ready && latest.action);

  const composer = (
    <form
      data-tour="assistant-composer"
      onSubmit={(e) => {
        e.preventDefault();
        send(input);
      }}
      className="flex items-end gap-2"
    >
      <MicButton
        track="mic-practice-assistant"
        onFinal={(t) => setInput((v) => (v ? `${v} ` : '') + t)}
      />
      <textarea
        ref={taRef}
        rows={1}
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onFocus={() => setExpanded(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            send(input);
          }
        }}
        placeholder={PLACEHOLDER}
        className="flex-1 resize-none px-3.5 py-2.5 border border-legal-300 rounded-xl text-sm focus:outline-none focus:ring-accent focus:border-accent max-h-32"
      />
      <button
        type="submit"
        data-track="assistant-send"
        disabled={!input.trim() || isThinking}
        aria-label="Send"
        className="p-2.5 bg-legal-900 text-accent rounded-xl hover:bg-legal-800 transition-colors disabled:opacity-40"
      >
        <Send className="h-4 w-4" />
      </button>
    </form>
  );

  // Collapsed: just the composer bar. Expands on focus / first message.
  if (!expanded) {
    return (
      <div className={`bg-white rounded-card border border-legal-200 shadow-elevation-2 p-3 ${className ?? ''}`}>
        {composer}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-card border border-legal-200 shadow-elevation-2 flex flex-col ${className ?? ''}`}>
      <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-legal-100">
        <p className="flex items-center gap-2 text-sm font-semibold text-legal-800">
          <Gavel className="h-4 w-4 text-accent-700" /> Practice assistant
        </p>
        <button
          type="button"
          onClick={() => setExpanded(false)}
          aria-label="Collapse assistant"
          className="p-1.5 rounded-md text-legal-400 hover:text-legal-700 hover:bg-legal-50 transition-colors"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      <div className="max-h-[22rem] overflow-y-auto px-4 py-3 space-y-4">
        {opener && <Bubble role="assistant">{opener}</Bubble>}
        {messages.length === 0 && !opener && (
          <Bubble role="assistant">
            Ask about your diary ("what's tomorrow?") or tell me what happened
            ("Ravi's case adjourned to 3 Aug, evidence closed") — I'll confirm before saving anything.
          </Bubble>
        )}

        {messages.map((m, i) => (
          <Bubble key={i} role={m.role}>{m.content}</Bubble>
        ))}

        {isThinking && (
          <Bubble role="assistant">
            <span className="inline-flex items-center gap-2 text-legal-400">
              <Loader2 className="h-3.5 w-3.5 animate-spin" /> checking the diary…
            </span>
          </Bubble>
        )}

        {latest?.artifact && !isThinking && (
          <div className="ml-4 sm:ml-10">
            <ArtifactCard artifact={latest.artifact} />
          </div>
        )}

        {/* action confirm card — nothing is saved until Confirm */}
        {showConfirm && latest?.action && (
          <div className="ml-4 sm:ml-10 border border-accent/40 bg-accent-50/70 rounded-2xl p-4 animate-slide-up">
            <p className="text-[11px] uppercase tracking-[0.18em] text-accent-700 mb-2 flex items-center gap-1.5">
              <ShieldQuestion className="h-3.5 w-3.5" /> Confirm — nothing saved yet
            </p>
            <ActionParams action={latest.action} caseTitle={caseTitle} />
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                type="button"
                data-track="assistant-confirm"
                disabled={isExecuting}
                onClick={confirmAction}
                className="inline-flex items-center gap-2 px-4 py-2 bg-accent text-legal-900 rounded-lg text-sm font-semibold hover:bg-accent-400 transition-colors disabled:opacity-50"
              >
                {isExecuting ? (
                  <><Loader2 className="h-4 w-4 animate-spin" /> Saving…</>
                ) : (
                  <><CheckCircle className="h-4 w-4" /> Confirm</>
                )}
              </button>
              <button
                type="button"
                onClick={cancelAction}
                className="inline-flex items-center gap-1.5 px-4 py-2 border border-legal-300 text-legal-600 rounded-lg text-sm hover:border-accent hover:text-accent-700 transition-colors"
              >
                <Pencil className="h-3.5 w-3.5" /> Cancel
              </button>
            </div>
          </div>
        )}

        {executed && (
          <p className="ml-4 sm:ml-10 flex items-start gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 break-words">
            <CheckCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{executed}</span>
          </p>
        )}

        {error && (
          <p className="ml-4 sm:ml-10 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 break-words">
            {error}
          </p>
        )}
        <div ref={endRef} />
      </div>

      <div className="border-t border-legal-100 p-3">{composer}</div>
    </div>
  );
}

/** Humanized <dl> of an action's params — ids are hidden (case resolves to its title). */
function ActionParams({
  action,
  caseTitle,
}: {
  action: AssistantAction;
  caseTitle: (id: string) => string | null;
}) {
  const isIdKey = (k: string) => /(^|_)id$/i.test(k);
  const humanize = (k: string) => k.replace(/_/g, ' ');
  const rows: { label: string; value: string }[] = [
    { label: 'action', value: humanize(action.type) },
  ];
  Object.entries(action.params ?? {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === '') return;
    if (isIdKey(key)) {
      if (key === 'matter_id') {
        const title = caseTitle(String(value));
        if (title) rows.push({ label: 'case', value: title });
      }
      return;
    }
    rows.push({
      label: humanize(key),
      value: typeof value === 'object' ? JSON.stringify(value) : String(value),
    });
  });
  return (
    <dl className="space-y-1.5">
      {rows.map((r) => (
        <div key={r.label} className="flex gap-2 text-sm">
          <dt className="text-legal-500 shrink-0 min-w-[7rem] capitalize">{r.label}</dt>
          <dd className="text-legal-900 break-words min-w-0">{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Structured rendering of a query artifact — compact rows with links. */
function ArtifactCard({ artifact }: { artifact: AssistantArtifact }) {
  const rows = Array.isArray(artifact.rows) ? artifact.rows : [];
  if (rows.length === 0) {
    return (
      <p className="text-xs text-legal-400 bg-legal-50 border border-legal-100 rounded-xl px-3 py-2">
        Nothing found for that.
      </p>
    );
  }

  const listTypes = ['hearings', 'cases', 'tasks', 'call_list'];
  if (listTypes.includes(artifact.type)) {
    return (
      <div className="bg-legal-50 border border-legal-100 rounded-xl divide-y divide-legal-100 overflow-hidden">
        {rows.slice(0, 12).map((row: any, i: number) => (
          <ArtifactRow key={i} type={artifact.type} row={row} />
        ))}
        {rows.length > 12 && (
          <p className="text-[11px] text-legal-400 px-3 py-1.5">…and {rows.length - 12} more</p>
        )}
      </div>
    );
  }

  // board / case_detail (and anything unrecognized): simple key-value cards.
  return (
    <div className="space-y-2">
      {rows.slice(0, 4).map((row: any, i: number) => (
        <dl key={i} className="bg-legal-50 border border-legal-100 rounded-xl px-3 py-2 space-y-1">
          {Object.entries(row ?? {})
            .filter(([k, v]) => v !== null && v !== undefined && v !== '' && !/(^|_)id$/i.test(k))
            .slice(0, 10)
            .map(([k, v]) => (
              <div key={k} className="flex gap-2 text-xs">
                <dt className="text-legal-400 shrink-0 min-w-[6.5rem] capitalize">
                  {k.replace(/_/g, ' ')}
                </dt>
                <dd className="text-legal-800 break-words min-w-0">
                  {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                </dd>
              </div>
            ))}
        </dl>
      ))}
    </div>
  );
}

function ArtifactRow({ type, row }: { type: string; row: any }) {
  const matterId: string | undefined = row?.matter_id || (type === 'cases' ? row?.id : undefined);

  let primary = '';
  let secondary: (string | null | undefined)[] = [];
  if (type === 'hearings') {
    primary = row?.case_title || row?.purpose || 'Hearing';
    secondary = [dayLabel(row?.hearing_date), row?.purpose, row?.status];
  } else if (type === 'cases') {
    primary = row?.title || row?.case_title || 'Case';
    secondary = [
      row?.client_name,
      row?.court_name,
      row?.case_number,
      row?.next_hearing_date ? `Next: ${dayLabel(row.next_hearing_date)}` : null,
    ];
  } else if (type === 'tasks') {
    primary = row?.title || 'Task';
    secondary = [
      row?.task_type ? String(row.task_type).replace(/_/g, ' ') : null,
      row?.due_date ? `Due ${dayLabel(row.due_date)}` : null,
      row?.status,
      row?.matter_title,
    ];
  } else {
    // call_list
    primary = row?.client?.name || row?.client_name || 'Client';
    secondary = [row?.case_title, dayLabel(row?.hearing_date)];
  }
  const sub = secondary.filter(Boolean).join(' · ');
  const phone = type === 'call_list' ? row?.client?.phone || row?.client?.whatsapp : null;

  const body = (
    <>
      <div className="min-w-0 flex-1">
        <p className="text-sm text-legal-800 truncate">{primary}</p>
        {sub && <p className="text-[11px] text-legal-400 truncate">{sub}</p>}
      </div>
    </>
  );

  return (
    <div className="flex items-center gap-2 px-3 py-2">
      {matterId ? (
        <Link
          to={`/app/cases/${matterId}`}
          className="flex items-center gap-2 min-w-0 flex-1 hover:text-accent-700"
        >
          {body}
        </Link>
      ) : (
        <div className="flex items-center gap-2 min-w-0 flex-1">{body}</div>
      )}
      {phone && (
        <>
          <a
            href={`tel:${phone}`}
            data-track="call-client"
            aria-label={`Call ${primary}`}
            className="shrink-0 p-2 rounded-lg border border-legal-200 text-legal-500 hover:border-accent hover:text-accent-700 transition-colors"
          >
            <Phone className="h-3.5 w-3.5" />
          </a>
          <a
            href={waLink(String(phone))}
            target="_blank"
            rel="noopener noreferrer"
            data-track="call-client"
            aria-label={`WhatsApp ${primary}`}
            className="shrink-0 p-2 rounded-lg border border-legal-200 text-[#25D366] hover:border-[#25D366] transition-colors text-xs font-semibold"
          >
            WA
          </a>
        </>
      )}
    </div>
  );
}

function Bubble({ role, children }: { role: 'user' | 'assistant'; children: React.ReactNode }) {
  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] bg-legal-900 text-legal-100 rounded-2xl rounded-br-md px-4 py-2.5 text-sm whitespace-pre-wrap break-words">
          {children}
        </div>
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2.5">
      <div className="h-7 w-7 rounded-full bg-accent-100 border border-accent/30 flex items-center justify-center flex-shrink-0 mt-0.5">
        <Gavel className="h-3.5 w-3.5 text-accent-700" />
      </div>
      <div className="min-w-0 max-w-[85%] bg-legal-50 border border-legal-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-sm text-legal-800 whitespace-pre-wrap break-words">
        {children}
      </div>
    </div>
  );
}
